import { createServerFn } from "@tanstack/react-start";
import {
  requireAuth,
  requireScopes,
  rateLimitFunction,
  createPostHogTrackingMiddleware,
} from "~/lib/middleware";
import {
  ApiResponse,
  Organization,
  organizationSchema,
  organizationStatusSchema,
} from "~/lib/schemas";
import z from "zod";
import { LogtoScopes } from "~/lib/logto/logto";
import { LogtoManagementApi } from "~/lib/logto/logto-management-api";
import { upsertGroup } from "~/lib/abodemail/generated/api";

// Request schemas - all fields optional for PATCH behavior
export const updateOrganizationSchema = z.object({
  organization_id: z.string().min(1, "Organization ID is required"),
  name: z
    .string()
    .min(1, "Organization name is required")
    .max(100, "Organization name must be less than 100 characters")
    .optional(),
  scopes: z.array(z.string()).optional(),
  status: organizationStatusSchema.optional(),
});

export type UpdateOrganizationRequest = z.infer<
  typeof updateOrganizationSchema
>;

export const updateOrganizationFn = createServerFn({
  method: "POST",
})
  .middleware([
    requireAuth,
    rateLimitFunction,
    requireScopes(LogtoScopes.AdminOrgCrud),  
    createPostHogTrackingMiddleware('update_organization')
  ])
  .validator(updateOrganizationSchema)
  .handler(async (ctx) => {
    const { organization_id, ...updates } = ctx.data;

    // Use real API
    const API_MANAGEMENT_BASE_URL =
      process.env.API_MANAGEMENT_BASE_URL || "http://localhost:8000";
    const BFF_API_KEY = process.env.API_MANAGEMENT_API_KEY;

    if (!BFF_API_KEY) {
      throw new Error(
        "API_MANAGEMENT_API_KEY environment variable is required",
      );
    }

    const params = new URLSearchParams({ organization_id });

    const response = await fetch(
      `${API_MANAGEMENT_BASE_URL}/api/v4/admin/organizations?${params}`,
      {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${BFF_API_KEY}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      },
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: "Unknown error" }));
      throw new Error(
        errorData.detail || `HTTP ${response.status}: ${response.statusText}`,
      );
    }

    const result = (await response.json()) as ApiResponse<Organization>;
    // console.log(result)
    let parsed = organizationSchema.parse(result.data);
    //update logto if logto is setup and if the source is logto
    if (process.env.LOGTO_MANAGEMENT_ENDPOINT && parsed.source === "LOGTO") {
      await LogtoManagementApi.updateOrganization(parsed.id, {
        name: parsed.name,
      });
    }
      await upsertGroup(parsed.id, {
        name: parsed.name,
    });


    return parsed;
  });
